import React, { useEffect, useRef, useState, memo } from "react";
import styles from "./OrderDetails.module.scss";
import { useDispatch } from "react-redux";
import {
  fetchPermitsForStore,
  finishOrderAction,
  preparedOrderAction,
  processOrderAction,
} from "../../../store/order-actions";
import { useReactToPrint } from "react-to-print";
import PrintOrderInfo from "../PrintComponents/PrintOrderInfo/PrintOrderInfo";
import PrintOrderDetails from "../../OrderProcessingComponents/PrintComponents/PrintOrderDetails/PrintOrderDetails";
import { orderActions } from "../../../store/order-slice";
import NotificationMessage from "../../UI/NotificationMessage/NotificationMessage";
import ConfirmationModal from "../../UI/Modals/ConfirmationModal/ConfirmationModal";
import ChatBubble from "../../UI/ChatBubble/ChatBubble";
import PrescriptionPhotos from "../PrescriptionPhotos/PrescriptionPhotos";
import Button from "../../UI/Buttons/Button/Button";
import { useMarketplaceInfo } from "../../../hooks/useMarketplaceInfo";
import LoyaltyCardInfo from "../LoyaltyCardInfo/LoyaltyCardInfo";
import ItemAvailabilityBadge from "../../shared/ItemAvailabilityBadge/ItemAvailabilityBadge";
import ImageModal from "../../shared/ImageModal/ImageModal";
import ItemAvailabilityDescriptionModal from "../../shared/ItemAvailabilityDescriptionModal/ItemAvailabilityDescriptionModal";
import { CnCOrderResponse } from "../../../types/api";
import { OrderItem } from "../../../types/shared";
import {
  isFriendPickup,
  isVolunteerDelivery,
  needsDeliveryPermit,
} from "../../../utils/deliveryType";

// ===== TYPE DEFINITIONS =====
// Using shared types from ../../../types/shared.ts and ../../../types/api.ts

interface OrderDetailsProps {
  order: CnCOrderResponse; // Replace local Order interface
  isAdmin: boolean;
  isDriver?: boolean;
  collapseOrder?: () => void;
  archived?: boolean;
}

// ===== UTILITY FUNCTIONS =====

const getDisplayPrice = (
  orderItem: OrderItem,
  isRFZOOrder: boolean,
): number => {
  // For RFZO orders, show price without discount (full price before subsidy)
  if (isRFZOOrder) {
    return orderItem.unitPriceWithoutDiscount || 0;
  }
  // For regular orders, use the final price (after any discounts)
  return orderItem.unitPrice != null
    ? orderItem.unitPrice
    : orderItem.unitPriceWithoutDiscount || 0;
};

const formatPrice = (price: number): string => {
  return price.toFixed(2);
};

// ===== COMPONENTS =====

interface OrderItemProps {
  orderItem: OrderItem;
  index: number;
  isServiceItem?: boolean;
  isRFZOOrder?: boolean;
}

const OrderItemComponent = memo<OrderItemProps>(
  ({ orderItem, index, isServiceItem = false, isRFZOOrder = false }) => {
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);
    const displayPrice = getDisplayPrice(orderItem, isRFZOOrder);
    const itemName = orderItem.itemResponse?.name || "Unknown Item";

    // Get the first IMAGE file (filter out videos) - images is deprecated
    const primaryFile = orderItem.itemResponse?.files?.find(
      (file) => file.type === "IMAGE",
    );
    const imageUrl = primaryFile
      ? `${process.env.REACT_APP_CDN_PATH}${primaryFile.location}`
      : null;

    // Only show image if it exists AND it's not a service item
    const shouldShowImage = imageUrl && !isServiceItem;

    const handleImageClick = () => {
      if (imageUrl) {
        setIsImageModalOpen(true);
      }
    };

    const handleCloseImageModal = () => {
      setIsImageModalOpen(false);
    };

    const handleBadgeClick = () => {
      setIsDescriptionModalOpen(true);
    };

    const handleCloseDescriptionModal = () => {
      setIsDescriptionModalOpen(false);
    };

    return (
      <>
        <div className={styles.OrderItem} key={index}>
          {shouldShowImage && (
            <img
              src={imageUrl}
              alt={itemName}
              className={styles.itemImage}
              onClick={handleImageClick}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleImageClick();
                }
              }}
            />
          )}

          <span
            className={styles.orderItemName}
            title={orderItem.itemExternalName || undefined}
          >
            {itemName}
            <ItemAvailabilityBadge
              shortCode={orderItem.itemResponse?.itemAvailabilityShortCode}
              color={orderItem.itemResponse?.itemAvailabilityColor}
              needRP={orderItem.itemResponse?.itemAvailabilityNeedRP}
              description={orderItem.itemResponse?.itemAvailabilityDescription}
              onClick={handleBadgeClick}
            />
          </span>

          {orderItem.price != null && (
            <span className={styles.countAndPrice}>
              {orderItem.quantity}x {formatPrice(displayPrice)} RSD
            </span>
          )}

          {/* Handle RFZO special pricing */}
          {orderItem.rfzoParticipationPrice && orderItem.rfzoQuantity && (
            <span className={styles.countAndPrice}>
              {orderItem.rfzoQuantity} kom. | {formatPrice(displayPrice)} RSD
            </span>
          )}
        </div>

        {imageUrl && (
          <ImageModal
            isOpen={isImageModalOpen}
            onClose={handleCloseImageModal}
            imageUrl={imageUrl}
            itemName={itemName}
            availabilityBadge={{
              shortCode: orderItem.itemResponse?.itemAvailabilityShortCode,
              color: orderItem.itemResponse?.itemAvailabilityColor,
              needRP: orderItem.itemResponse?.itemAvailabilityNeedRP,
              description: orderItem.itemResponse?.itemAvailabilityDescription,
            }}
          />
        )}

        <ItemAvailabilityDescriptionModal
          isOpen={isDescriptionModalOpen}
          onClose={handleCloseDescriptionModal}
          description={orderItem.itemResponse?.itemAvailabilityDescription}
          shortCode={orderItem.itemResponse?.itemAvailabilityShortCode}
          color={orderItem.itemResponse?.itemAvailabilityColor}
          needRP={orderItem.itemResponse?.itemAvailabilityNeedRP}
        />
      </>
    );
  },
);

OrderItemComponent.displayName = "OrderItemComponent";

interface OrderItemsListProps {
  items: OrderItem[];
  title?: string;
  className?: string;
  isServiceItems?: boolean;
  isRFZOOrder?: boolean;
}

const OrderItemsList = memo<OrderItemsListProps>(
  ({
    items,
    title,
    className,
    isServiceItems = false,
    isRFZOOrder = false,
  }) => {
    if (!items || items.length === 0) {
      return null;
    }

    return (
      <div className={className}>
        {title && (
          <div className={styles.ItemsSectionTitle}>
            <strong>{title}</strong>
          </div>
        )}
        {items.map((orderItem, index) => (
          <OrderItemComponent
            key={`${orderItem.itemResponse?.id || "item"}-${index}`}
            orderItem={orderItem}
            index={index}
            isServiceItem={isServiceItems}
            isRFZOOrder={isRFZOOrder}
          />
        ))}
      </div>
    );
  },
);

OrderItemsList.displayName = "OrderItemsList";

// ===== MAIN COMPONENT =====

const OrderDetails: React.FC<OrderDetailsProps> = ({
  order,
  isAdmin,
  collapseOrder,
  archived,
}) => {
  const dispatch = useDispatch();

  const [confirmationModalVisible, setConfirmationModalVisible] =
    useState(false);
  const [noteForUser, setNoteForUser] = useState<string>("");
  const [notificationMessageVisible, setNotificationMessageVisible] =
    useState(false);
  const [permitForStore, setPermitForStore] = useState<string | null>(null);

  const printOrderInfoRef = useRef<HTMLDivElement>(null);
  const printOrderDetailsRef = useRef<HTMLDivElement>(null);

  const {
    branchMarketplaceId,
    isReady,
    isAdmin: isAdminMarketplace,
  } = useMarketplaceInfo();

  // ===== EVENT HANDLERS =====

  const closeModal = () => {
    collapseOrder && collapseOrder();
    dispatch(orderActions.toggleOrderModal(null));
  };

  const closeConfirmationModal = () => {
    setConfirmationModalVisible(false);
  };

  const openConfirmationModal = () => {
    setConfirmationModalVisible(true);
  };

  const orderPreparedAction = () => {
    dispatch<any>(preparedOrderAction(order.id));
    closeModal();
  };

  const takenOrderAction = () => {
    let nextStatus = "TAKEN";
    if (isVolunteerDelivery(order)) {
      nextStatus = "IN_DELIVERY";
    }

    dispatch<any>(
      finishOrderAction(
        order.id,
        nextStatus,
        "noteForUser",
        order.orderVerificationCode,
      ),
    );
    closeModal();
  };

  const acceptOrder = (willBeReadyIn: number) => {
    dispatch<any>(processOrderAction(order.id, noteForUser, willBeReadyIn));
    closeModal();
  };

  const getNoteForUser = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNoteForUser(e.target.value);
  };

  const printOrderInfoAction = useReactToPrint({
    content: () => printOrderInfoRef.current!,
    // The slip is handed to the patient and the browser prints document.title
    // into the page margin, so pin it rather than leaking the tab title, which
    // becomes "Dolazni poziv | apoteka.rs" while a consult is ringing.
    documentTitle: `Rezervacija ${order.id}`,
  });

  const printOrderDetailsAction = useReactToPrint({
    content: () => printOrderDetailsRef.current!,
  });

  const copyLBO = () => {
    if (order.userLBO) {
      navigator.clipboard.writeText(order.userLBO);
      setNotificationMessageVisible(true);
      setTimeout(() => {
        setNotificationMessageVisible(false);
      }, 5000);
    }
  };

  // ===== EFFECTS =====

  useEffect(() => {
    const fetchOrderPermits = async () => {
      if (isVolunteerDelivery(order)) {
        if (order?.id && order?.userDeliveryResponse?.id) {
          try {
            const response = await fetchPermitsForStore(
              order.id,
              order.userDeliveryResponse.id,
            );
            const folderPath = response?.data?.signed
              ? "user_delivery_permits"
              : "delivery_permits";
            const fileName = response?.data?.signed
              ? response?.data?.signedPermitFileName
              : "p" + response?.data?.id + ".pdf";
            setPermitForStore(
              process.env.REACT_APP_CDN_PATH + folderPath + "/" + fileName,
            );
          } catch (error) {
            console.error("Failed to fetch permits:", error);
            // Permits are optional for pharmacy workflow - don't break the component
          }
        }
      }
      if (isFriendPickup(order)) {
        setPermitForStore(
          process.env.REACT_APP_CDN_PATH +
            "delivery_permits/o" +
            order?.id +
            ".pdf",
        );
      }
    };

    if (order?.userDeliveryResponse) {
      fetchOrderPermits().catch(console.error);
    }
  }, [
    order.deliveryType,
    order?.userDeliveryResponse?.id,
    order?.id,
    order?.userDeliveryResponse,
  ]);

  // ===== RENDER =====

  const hasServiceItems = order.serviceItems && order.serviceItems.length > 0;
  const isRFZOOrder = !!order.rfzoTherapyNumber;

  return (
    <>
      <div className={styles.OrderModal}>
        <div>
          Tel:{" "}
          {order.userPhoneNumberForOrder || order?.userResponse?.phoneNumber}
        </div>
        {order?.userLBO && <div>LBO: {order?.userLBO}</div>}
        {order?.userJmbg && <div>JMBG: {order?.userJmbg}</div>}
        <div className={`${styles.Column} ${styles.RightColumn}`}>
          <RightPart
            order={order}
            copyLBO={copyLBO}
            getNoteForUser={getNoteForUser}
            permitForStore={permitForStore}
            printOrderDetailsAction={printOrderDetailsAction}
            isAdmin={isAdmin}
            openConfirmationModal={openConfirmationModal}
            printOrderInfoAction={printOrderInfoAction}
            takenOrderAction={takenOrderAction}
            orderPreparedAction={orderPreparedAction}
            closeModal={closeModal}
            isAdminHook={isAdminMarketplace}
            isReadyHook={isReady}
          />

          {/* Regular Items */}
          <OrderItemsList
            items={order.items}
            className={styles.Items}
            isServiceItems={false}
            isRFZOOrder={isRFZOOrder}
          />

          {/* Service Items - Display below regular items */}
          {hasServiceItems && (
            <OrderItemsList
              items={order.serviceItems!}
              className={styles.ServiceItems}
              isServiceItems={true}
              isRFZOOrder={isRFZOOrder}
            />
          )}

          <div className={styles.OrderModalFooter}>
            <span>Ukupna cena:</span>
            <strong>
              {formatPrice(
                isRFZOOrder
                  ? order.totalPriceWithoutDiscount
                  : order.totalPrice,
              )}{" "}
              RSD
            </strong>
          </div>
        </div>

        {order.status === "CREATED" && (
          <>
            <label htmlFor="NoteFromUser" className={styles.Label}>
              Poruka za korisnika
            </label>
            <div id="NoteForUser" className={styles.NoteForUser}>
              <div>
                <textarea
                  placeholder="Tekst poruke....."
                  className={styles.TextArea}
                  onChange={getNoteForUser}
                  value={noteForUser}
                ></textarea>
              </div>
            </div>
          </>
        )}

        {!archived && !isAdmin && (
          <ProccessingButtons
            order={order}
            printOrderDetailsAction={printOrderDetailsAction}
            isAdmin={isAdmin}
            acceptOrder={acceptOrder}
            openConfirmationModal={openConfirmationModal}
            printOrderInfoAction={printOrderInfoAction}
            takenOrderAction={takenOrderAction}
            orderPreparedAction={orderPreparedAction}
            closeModal={closeModal}
          />
        )}
      </div>

      {confirmationModalVisible && (
        <ConfirmationModal
          show={confirmationModalVisible}
          backdropClickHandler={closeConfirmationModal}
          order={order}
          noteForUser={noteForUser}
        />
      )}

      <div style={{ display: "none" }}>
        <PrintOrderDetails ref={printOrderDetailsRef} order={order} />
      </div>
      <div style={{ display: "none" }}>
        <PrintOrderInfo ref={printOrderInfoRef} order={order} />
      </div>

      {notificationMessageVisible && (
        <NotificationMessage>LBO kopiran!</NotificationMessage>
      )}
    </>
  );
};

export default OrderDetails;

// ===== EXISTING COMPONENTS (Updated with proper typing) =====

interface RightPartProps {
  order: CnCOrderResponse;
  permitForStore: string | null;
  isAdminHook: boolean;
  isReadyHook: boolean;
  copyLBO: () => void;
  getNoteForUser: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  printOrderDetailsAction: () => void;
  isAdmin: boolean;
  openConfirmationModal: () => void;
  printOrderInfoAction: () => void;
  takenOrderAction: () => void;
  orderPreparedAction: () => void;
  closeModal: () => void;
}

const RightPart: React.FC<RightPartProps> = ({
  order,
  permitForStore,
  isAdminHook,
  isReadyHook,
}) => {
  const showDeliveryPermit = permitForStore && needsDeliveryPermit(order);

  return (
    <div className={styles.RightColumnContentWrapper}>
      <div className={styles.UpperPart}>
        <div id="NoteFromUser" className={styles.ChatBubble}>
          <ChatBubble noteFromUserFlag>
            {order.noteForMarketplace ? (
              order.noteForMarketplace
            ) : (
              <span className={styles.NoNoteFromUser}>
                Korisnik nije uneo napomenu
              </span>
            )}
          </ChatBubble>
        </div>
        {(order.status === "PROCESSED" ||
          order.status === "PREPARED" ||
          order.status === "REJECTED" ||
          order.status === "TAKEN") &&
          order.noteForUser && <ChatBubble>{order.noteForUser}</ChatBubble>}
        {order.status === "CANCELED" && (
          <ChatBubble order={order}>{order.noteForCanceledStatus}</ChatBubble>
        )}
        <PrescriptionPhotos
          permitForStore={showDeliveryPermit ? permitForStore : null}
          prescriptionsLinks={order.prescriptionsLinks}
        />
        {isVolunteerDelivery(order) &&
          order.orderVerificationCode &&
          (order.status === "PROCESSED" || order.status === "PREPARED") && (
            <div className={styles.verificationCodeSection}>
              <div className={styles.verificationCodeLabel}>
                Verifikacioni kod za preuzimanje:
              </div>
              <div className={styles.verificationCodeValue}>
                {order.orderVerificationCode}
              </div>
              <div className={styles.verificationCodeNote}>
                Apoteka treba da prosledi ovaj kod ovlašćenom licu prilikom
                preuzimanja rezervacije
              </div>
            </div>
          )}
        {!isAdminHook && isReadyHook && order?.userResponse?.id && (
          <LoyaltyCardInfo userId={order.userResponse.id} />
        )}
      </div>

      {isFriendPickup(order) && (
        <DeliveryPersonDetails
          firstName={order?.deliveryFriendFirstName}
          secondName={order?.deliveryFriendSecondName}
          phoneNumber={order?.deliveryFriendPhoneNumber}
          personalId={order?.deliveryFriendPersonalId}
        />
      )}
      {(order.deliveryType === "FAST_DELIVERY" ||
        order.deliveryType === "STANDARD_DELIVERY") &&
        order.userDeliveryResponse && (
          <DeliveryPersonDetails
            firstName={order?.userDeliveryResponse?.firstName}
            secondName={order?.userDeliveryResponse?.secondName}
            phoneNumber={order?.userDeliveryResponse?.phoneNumber}
            personalId={order?.userDeliveryResponse?.personalId}
          />
        )}
    </div>
  );
};

interface ProcessingButtonsProps {
  order: CnCOrderResponse;
  printOrderDetailsAction: () => void;
  printOrderInfoAction: () => void;
  isAdmin: boolean;
  acceptOrder: (willBeReadyIn: number) => void;
  openConfirmationModal: () => void;
  orderPreparedAction: () => void;
  takenOrderAction: () => void;
  closeModal: () => void;
}

const ProccessingButtons: React.FC<ProcessingButtonsProps> = ({
  order,
  printOrderDetailsAction,
  printOrderInfoAction,
  isAdmin,
  acceptOrder,
  openConfirmationModal,
  orderPreparedAction,
  takenOrderAction,
  closeModal,
}) => {
  return (
    <div className={styles.OrderProcessingPart}>
      {order.status === "CREATED" && (
        <div className={styles.PrintActionsWrapper}>
          <Button
            buttonType="white"
            style={{ fontsize: "14px", margin: "10px 0" }}
            onClick={printOrderDetailsAction}
          >
            Lista za pakovanje
          </Button>
          <Button
            buttonType="white"
            style={{ fontsize: "14px", margin: "10px 0" }}
            onClick={printOrderInfoAction}
          >
            Detalji rezervacije
          </Button>
        </div>
      )}
      <>
        {order.status === "CREATED" && (
          <>
            <Button
              buttonType="blue"
              style={{ fontsize: "14px", margin: "10px 0" }}
              onClick={() => acceptOrder(20)}
              disabled={isAdmin}
            >
              Prihvati rezervaciju
            </Button>
            <Button
              buttonType="white"
              style={{ fontsize: "14px", margin: "10px 0" }}
              onClick={openConfirmationModal}
              disabled={isAdmin}
            >
              Odbaci rezervaciju
            </Button>
          </>
        )}
        {order.status === "PROCESSED" && (
          <>
            <div className={styles.PrintActionsWrapper}>
              <Button
                buttonType="white"
                style={{ fontsize: "14px", margin: "10px 0" }}
                onClick={printOrderInfoAction}
              >
                Detalji rezervacije
              </Button>
            </div>
            <Button
              buttonType="blue"
              style={{ fontsize: "14px", margin: "10px 0" }}
              onClick={takenOrderAction}
              disabled={isAdmin}
            >
              Preuzeto
            </Button>
            <Button
              buttonType="white"
              style={{ fontsize: "14px", margin: "10px 0" }}
              onClick={openConfirmationModal}
              disabled={isAdmin}
            >
              Otkaži rezervaciju
            </Button>
          </>
        )}
        {order.status === "PREPARED" && (
          <>
            <div className={styles.PrintActionsWrapper}>
              <Button
                buttonType="white"
                style={{ fontsize: "14px", margin: "10px 0" }}
                onClick={printOrderInfoAction}
              >
                Detalji rezervacije
              </Button>
            </div>
            <Button
              buttonType="blue"
              style={{ fontsize: "14px", margin: "10px 0" }}
              onClick={takenOrderAction}
              disabled={isAdmin}
            >
              Preuzeto
            </Button>
            <Button
              buttonType="white"
              style={{ fontsize: "14px", margin: "10px 0" }}
              onClick={openConfirmationModal}
              disabled={isAdmin}
            >
              Otkaži rezervaciju
            </Button>
          </>
        )}
        {(order.status === "TAKEN" ||
          order.status === "REJECTED" ||
          order.status === "CANCELED") && (
          <>
            <Button
              style={{ fontsize: "14px", margin: "10px 0" }}
              buttonType="blue"
              onClick={closeModal}
            >
              Zatvori
            </Button>
          </>
        )}
      </>
    </div>
  );
};

interface DeliveryPersonDetailsProps {
  firstName?: string;
  secondName?: string;
  phoneNumber?: string;
  personalId?: string;
}

const DeliveryPersonDetails: React.FC<DeliveryPersonDetailsProps> = ({
  firstName,
  secondName,
  phoneNumber,
  personalId,
}) => {
  return (
    <div className={styles.deliveryPersonDetails}>
      <div className={styles.header}>Rezervaciju ce preuzeti:</div>

      <div className={styles.infoItem}>
        <div>Ime i prezime:</div>
        <strong>
          {firstName} {secondName}
        </strong>
      </div>
      <div className={styles.infoItem}>
        <div>Broj telefona:</div>
        <strong>{phoneNumber}</strong>
      </div>
      <div className={styles.infoItem}>
        <div>Broj ličnog dokumenta:</div>
        <strong>{personalId}</strong>
      </div>
    </div>
  );
};
